import React, { FC, useState, useRef, useEffect } from 'react';
import { Button, Card, message } from 'antd';
import { AudioOutlined, StopOutlined } from '@ant-design/icons';
import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';
import { ListeningTypeSelector } from '../ListeningTypeSelector/ListeningTypeSelector';
import { uploadAudioChunk, clearSession } from '../../api/speechToInsightsApi';

interface AudioMicTabProps {
    setIsProcessing: (value: boolean) => void;
    setTranscriptContent: (transcript: string) => void;
}

export const AudioMicTab: FC<AudioMicTabProps> = ({ setIsProcessing, setTranscriptContent }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [listeningType, setListeningType] = useState('');
    const realTimeTranscriptRef = useRef<string>('');

    const recognizerRef = useRef<speechsdk.SpeechRecognizer | null>(null);

    const audioStreamRef = useRef<MediaStream | null>(null);
    const recordingIntervalRef = useRef<NodeJS.Timer | null>(null);

    const handleListeningTypeChange = (value: string) => {
        setListeningType(value);
    };

    const createAndStartRecorder = (stream: MediaStream) => {
        const chunks: Blob[] = [];
        const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'audio/webm;codecs=opus'
        });

        mediaRecorder.ondataavailable = (event: BlobEvent) => {
            if (event.data && event.data.size > 0) {
                chunks.push(event.data);
            }
        };

        mediaRecorder.onstop = async () => {
            if (chunks.length > 0) {
                try {
                    const audioBlob = new Blob(chunks, { type: 'audio/webm;codecs=opus' });
                    await uploadAudioChunk(audioBlob, listeningType);
                    console.log('Audio chunk enviado');
                } catch (err) {
                    console.error('Error enviando chunk de audio:', err);
                }
            }
        };

        setTimeout(() => {
            if (mediaRecorder.state !== 'inactive') {
                mediaRecorder.stop();
            }
        }, parseInt(process.env.REACT_APP_AUDIO_CHUNKS_MS || '10000'));

        mediaRecorder.start();
        return mediaRecorder;
    };

    const initializeSpeechRecognizer = () => {
        const speechConfig = speechsdk.SpeechConfig.fromSubscription(
            process.env.REACT_APP_SPEECH_KEY || '',
            process.env.REACT_APP_SPEECH_REGION || ''
        );
        speechConfig.speechRecognitionLanguage = 'es-ES';

        const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
        const recognizer = new speechsdk.SpeechRecognizer(speechConfig, audioConfig);

        recognizer.recognized = (s, e) => {
            if (e.result.text) {
                realTimeTranscriptRef.current += '\n' + e.result.text;
                setTranscriptContent(realTimeTranscriptRef.current);
            }
        };

        recognizer.recognizing = (s, e) => {
            if (e.result.text) {
                setTranscriptContent(realTimeTranscriptRef.current + '\n' + e.result.text);
            }
        };

        recognizerRef.current = recognizer;
    };

    const handleRecord = async () => {
        try {
            setIsRecording(true);
            setIsProcessing(true);

            realTimeTranscriptRef.current = '';
            setTranscriptContent('');
            await clearSession();

            initializeSpeechRecognizer();
            recognizerRef.current?.startContinuousRecognitionAsync();

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioStreamRef.current = stream;
            createAndStartRecorder(stream);
            recordingIntervalRef.current = setInterval(() => {
                if (audioStreamRef.current) {
                    createAndStartRecorder(audioStreamRef.current);
                }
            }, parseInt(process.env.REACT_APP_AUDIO_CHUNKS_MS || '10000'));

            message.info('Grabación iniciada');
        } catch (error) {
            console.error('Error al iniciar la grabación:', error);
            message.error('No se pudo iniciar la grabación');
        }
    };

    const handleStop = () => {
        recognizerRef.current?.stopContinuousRecognitionAsync();
        recognizerRef.current = null;

        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }

        if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
            audioStreamRef.current = null;
        }

        setIsRecording(false);
        setIsProcessing(false);
        message.success('Grabación detenida');
    };

    useEffect(() => {
        return () => {
            realTimeTranscriptRef.current = '';
        };
    }, []);

    return (
        <Card>
            <ListeningTypeSelector value={listeningType} onChange={handleListeningTypeChange} />
            <Button
                type="primary"
                icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
                onClick={isRecording ? handleStop : handleRecord}
                block
            >
                {isRecording ? 'Detener Grabación' : 'Iniciar Grabación'}
            </Button>
        </Card>
    );
};
